import type { ReactNode } from 'react';
import { Card, MicroLabel } from './ui/primitives';

/**
 * Who the quote is for, and who else can open it.
 *
 * Guest access sits inside the client card because it answers the same
 * question — who can see this quote — and a guest link is quote-wide rather
 * than per offer, so it is stated once here rather than on every offer.
 */
export function QuoteClient({
	name,
	meta,
	avatar,
	action,
	guestAccess,
}: {
	name: string;
	/** "Organization · Stavanger, NO · client since 2024". */
	meta?: ReactNode;
	avatar?: ReactNode;
	/** "Open client". */
	action?: ReactNode;
	/**
	 * Guest access, as one line: how many links are out and a way to send
	 * another. Deliberately a summary and not a panel — the design keeps this
	 * card to two rows, and a list of tokens with their own statuses is a
	 * different job from "who is this quote for".
	 */
	guestAccess?: { summary: ReactNode; action?: ReactNode };
}) {
	return (
		<Card padded>
			<div className="flex flex-wrap items-center gap-3">
				{avatar}
				<div className="min-w-[200px] flex-1">
					<div className="text-base font-medium text-text-1">{name}</div>
					{meta && <div className="mt-px text-sm text-text-3">{meta}</div>}
				</div>
				{action}
			</div>
			{guestAccess && (
				<div className="mt-3 flex flex-wrap items-center gap-2.5 border-t border-line-1 pt-3">
					<MicroLabel>Guest access</MicroLabel>
					<span className="min-w-0 flex-1 text-sm text-text-4">
						{guestAccess.summary}
					</span>
					{guestAccess.action}
				</div>
			)}
		</Card>
	);
}
